// =============================================================================
// pending-effect-queue.ts — pure-function port of Services/PendingEffectQueue.cs
// No class, no singleton, no module-level state.
//
// Per-seat FIFO of one-shot consumable build/draw modifiers.
// State: Readonly<Record<string, ReadonlyArray<PendingEffect>>>.
// Pre-seeded seats "0".."3" + global sentinel "-1" on init (matching Unity Awake).
//
// Note: Unity source does NOT pre-seed "-1" in PEQ.Awake() (only 0-3).
// Global support is added here for future parity only; base tests don't use it.
// =============================================================================

import type { FacilityCategory, FacilityType, PendingKind } from "@gdg/shared";
import { deepFreeze } from "../_freeze.js";
import type { ActionLog } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";

// ---------------------------------------------------------------------------
// PendingEffect (ported from PendingEffectQueue.cs struct)
// ---------------------------------------------------------------------------

export interface PendingEffect {
  readonly ownerSeat: number;
  readonly kind: PendingKind;
  readonly valueInt: number;
  readonly sourceCardName: string;
  /** 0 = no tracked instance (echoes CIS instanceId for Whenever-discard cards) */
  readonly instanceId: number;
  /** null/undefined = any category */
  readonly categoryFilter?: FacilityCategory | null;
  /** null/undefined = any facility type */
  readonly facilityFilter?: FacilityType | null;
}

// ---------------------------------------------------------------------------
// PendingEffectQueueState
// ---------------------------------------------------------------------------

export interface PendingEffectQueueState {
  /** keyed by stringified ownerSeat; "0"-"3" + optional "-1" global */
  readonly queues: Readonly<Record<string, ReadonlyArray<PendingEffect>>>;
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface EnqueueResult {
  readonly newState: PendingEffectQueueState;
  readonly log: ActionLog;
}

export interface TryConsumeResult {
  readonly ok: boolean;
  readonly newState: PendingEffectQueueState;
  readonly log: ActionLog;
  readonly consumed?: PendingEffect;
}

export interface DrainBumpsResult {
  readonly newState: PendingEffectQueueState;
  readonly log: ActionLog;
  /** Number of bumps drained. */
  readonly count: number;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/**
 * Create initial state pre-seeding seats 0-3.
 * Mirrors Unity PendingEffectQueue.Awake().
 */
export function createPendingEffectQueueState(): PendingEffectQueueState {
  const empty: ReadonlyArray<PendingEffect> = Object.freeze([]);
  return Object.freeze({
    queues: Object.freeze({
      "0": empty,
      "1": empty,
      "2": empty,
      "3": empty,
    }),
  });
}

// ---------------------------------------------------------------------------
// Filter helpers (ported from Unity static CategoryMatches / FacilityMatches)
// ---------------------------------------------------------------------------

function categoryMatches(
  filter: FacilityCategory | null | undefined,
  actual: FacilityCategory | null | undefined,
): boolean {
  if (filter === null || filter === undefined) return true;
  if (actual === null || actual === undefined) return false;
  return filter === actual;
}

function facilityMatches(
  filter: FacilityType | null | undefined,
  actual: FacilityType | null | undefined,
): boolean {
  if (filter === null || filter === undefined) return true;
  if (actual === null || actual === undefined) return false;
  return filter === actual;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Add a PendingEffect to the queue for its ownerSeat.
 * Creates the bucket on demand if not pre-seeded.
 * Mirrors Unity PendingEffectQueue.Enqueue(pe).
 */
export function enqueue(
  state: PendingEffectQueueState,
  log: ActionLog,
  pe: PendingEffect,
): EnqueueResult {
  assertFiniteInt(pe.ownerSeat, "enqueue pe.ownerSeat");

  const key = String(pe.ownerSeat);
  const existing: ReadonlyArray<PendingEffect> = state.queues[key] ?? Object.freeze([]);
  const frozenPe = deepFreeze(pe);
  const updated: ReadonlyArray<PendingEffect> = Object.freeze([...existing, frozenPe]);
  const newQueues = Object.freeze({ ...state.queues, [key]: updated });
  const newState: PendingEffectQueueState = Object.freeze({ queues: newQueues });
  const nextLog = log.append({
    kind: "pending.enqueue",
    data: {
      seat: pe.ownerSeat,
      kind: pe.kind,
      value: pe.valueInt,
      cardId: pe.sourceCardName,
    },
  });
  return { newState, log: nextLog };
}

/**
 * Remove and return the first matching PendingEffect from the seat's queue.
 * Matching: kind must match, categoryFilter AND facilityFilter must match.
 * Priority: seat-local bucket first, then global "-1" bucket.
 * Mirrors Unity PendingEffectQueue.TryConsume(seatIndex, kind, facilityCat, facilityType?).
 */
export function tryConsume(
  state: PendingEffectQueueState,
  log: ActionLog,
  seatIndex: number,
  kind: PendingKind,
  facilityCat: FacilityCategory | null | undefined,
  facilityType?: FacilityType | null,
): TryConsumeResult {
  assertFiniteInt(seatIndex, "tryConsume seatIndex");

  // Check seat-local bucket first, then global "-1" bucket.
  const bucketsToCheck: string[] = seatIndex === -1 ? ["-1"] : [String(seatIndex), "-1"];

  for (const key of bucketsToCheck) {
    const q: ReadonlyArray<PendingEffect> = state.queues[key] ?? Object.freeze([]);

    for (let i = 0; i < q.length; i++) {
      const pe = q[i];
      if (pe === undefined) continue;
      if (pe.kind !== kind) continue;
      if (!categoryMatches(pe.categoryFilter, facilityCat)) continue;
      if (!facilityMatches(pe.facilityFilter, facilityType)) continue;

      // Found — remove it from whichever bucket matched.
      const newQ: ReadonlyArray<PendingEffect> = Object.freeze([
        ...q.slice(0, i),
        ...q.slice(i + 1),
      ]);
      const newQueues = Object.freeze({ ...state.queues, [key]: newQ });
      const newState: PendingEffectQueueState = Object.freeze({ queues: newQueues });
      const nextLog = log.append({
        kind: "pending.consume",
        data: {
          seat: pe.ownerSeat,
          kind: pe.kind,
          value: pe.valueInt,
          cardId: pe.sourceCardName,
        },
      });
      return { ok: true, newState, log: nextLog, consumed: pe };
    }
  }

  return { ok: false, newState: state, log };
}

/**
 * Drain ALL NextBuildCostBump entries that match the facility category.
 * Drains from both seat-local bucket and global "-1" bucket.
 * Returns aggregated count of drained entries.
 * Mirrors Unity PendingEffectQueue.DrainBumps(seatIndex, facilityCat).
 */
export function drainBumps(
  state: PendingEffectQueueState,
  log: ActionLog,
  seatIndex: number,
  facilityCat: FacilityCategory | null | undefined,
  facilityType?: FacilityType | null,
): DrainBumpsResult {
  assertFiniteInt(seatIndex, "drainBumps seatIndex");

  // Drain from seat-local bucket and global "-1" bucket.
  const bucketsToCheck: string[] = seatIndex === -1 ? ["-1"] : [String(seatIndex), "-1"];

  let currentState = state;
  let currentLog = log;
  let totalCount = 0;

  for (const key of bucketsToCheck) {
    const q: ReadonlyArray<PendingEffect> = currentState.queues[key] ?? Object.freeze([]);
    const remaining: PendingEffect[] = [];
    let bucketCount = 0;

    for (const pe of q) {
      if (
        pe.kind === "NextBuildCostBump" &&
        categoryMatches(pe.categoryFilter, facilityCat) &&
        // Codex WEB.4.D R1 HIGH fix: thread facilityFilter for type-specific bumps
        // (Drought NextBuildCostBump Hydro). Closes WEB.4.1 R2 Low.
        facilityMatches(pe.facilityFilter, facilityType ?? null)
      ) {
        bucketCount++;
        currentLog = currentLog.append({
          kind: "pending.drain",
          data: {
            seat: pe.ownerSeat,
            kind: pe.kind,
            value: pe.valueInt,
            cardId: pe.sourceCardName,
          },
        });
      } else {
        remaining.push(pe);
      }
    }

    if (bucketCount > 0) {
      totalCount += bucketCount;
      const newQueues = Object.freeze({ ...currentState.queues, [key]: Object.freeze(remaining) });
      currentState = Object.freeze({ queues: newQueues });
    }
  }

  if (totalCount === 0) {
    return { newState: state, log, count: 0 };
  }

  return { newState: currentState, log: currentLog, count: totalCount };
}

/**
 * Read-only view of the queue for a seat (no mutation).
 * Mirrors Unity PendingEffectQueue.Peek(seatIndex).
 */
export function peek(
  state: PendingEffectQueueState,
  seatIndex: number,
): ReadonlyArray<PendingEffect> {
  assertFiniteInt(seatIndex, "peek seatIndex");
  return state.queues[String(seatIndex)] ?? Object.freeze([]);
}

/**
 * Sum of all NextBuildCostBump values for a seat/category.
 * Read-only — does NOT consume.
 * Includes entries from global "-1" bucket as well as seat-local bucket.
 * Mirrors Unity PendingEffectQueue.SumActiveBumps(seatIndex, facilityCat).
 */
export function sumActiveBumps(
  state: PendingEffectQueueState,
  seatIndex: number,
  facilityCat: FacilityCategory | null | undefined,
  facilityType?: FacilityType | null,
): number {
  assertFiniteInt(seatIndex, "sumActiveBumps seatIndex");

  const bucketsToCheck: string[] = seatIndex === -1 ? ["-1"] : [String(seatIndex), "-1"];

  let total = 0;
  for (const key of bucketsToCheck) {
    const q: ReadonlyArray<PendingEffect> = state.queues[key] ?? Object.freeze([]);
    for (const pe of q) {
      if (pe.kind !== "NextBuildCostBump") continue;
      if (!categoryMatches(pe.categoryFilter, facilityCat)) continue;
      // Codex WEB.4.D R1 HIGH fix: thread facilityFilter (Drought Hydro-only bump).
      if (!facilityMatches(pe.facilityFilter, facilityType ?? null)) continue;
      total += pe.valueInt;
    }
  }
  return total;
}

/**
 * Read-only view of the queue for a seat, including global "-1" bucket.
 * Aggregates seat-local bucket first, then global entries (matches tryConsume priority).
 * Use instead of peek() for cost calculations that must respect global PE discounts.
 */
export function peekIncludingGlobal(
  state: PendingEffectQueueState,
  seatIndex: number,
): ReadonlyArray<PendingEffect> {
  assertFiniteInt(seatIndex, "peekIncludingGlobal seatIndex");
  const local: ReadonlyArray<PendingEffect> = state.queues[String(seatIndex)] ?? Object.freeze([]);
  const global: ReadonlyArray<PendingEffect> = state.queues["-1"] ?? Object.freeze([]);
  return Object.freeze([...local, ...global]);
}

/**
 * True if any non-Bump discount kind is queued for the seat/category/type.
 * Checks both seat-local bucket and global "-1" bucket.
 * Mirrors Unity PendingEffectQueue.HasAnyDiscount(seatIndex, facilityCat, facilityType?).
 */
export function hasAnyDiscount(
  state: PendingEffectQueueState,
  seatIndex: number,
  facilityCat: FacilityCategory | null | undefined,
  facilityType?: FacilityType | null,
): boolean {
  assertFiniteInt(seatIndex, "hasAnyDiscount seatIndex");

  const bucketsToCheck: string[] = seatIndex === -1 ? ["-1"] : [String(seatIndex), "-1"];

  for (const key of bucketsToCheck) {
    const q: ReadonlyArray<PendingEffect> = state.queues[key] ?? Object.freeze([]);
    for (const pe of q) {
      const isDiscountKind =
        pe.kind === "NextBuildFree" ||
        pe.kind === "NextBuildDiscount" ||
        pe.kind === "NextSolarFree" ||
        pe.kind === "NextNuclearDiscount";
      if (!isDiscountKind) continue;
      if (!categoryMatches(pe.categoryFilter, facilityCat)) continue;
      if (!facilityMatches(pe.facilityFilter, facilityType)) continue;
      return true;
    }
  }
  return false;
}
